import type { FormFieldSourceConfig, SelectOption } from '@commenda/types';
import { useMemo } from 'react';
import { ENTRY_ID_KEY, PERSON_LABELS_KEY } from '../dsl-constants';

interface UseFormFieldOptionsParams {
  config: FormFieldSourceConfig | undefined;
  formData: Record<string, unknown>;
  skip?: boolean;
}

type KeyPersonsValue = {
  selectedIds?: unknown;
  entries?: Record<string, unknown>;
  [PERSON_LABELS_KEY]?: Record<string, unknown>;
};

/** A KeyPersons field stores `{ selectedIds, entries }`; a repeatable module stores an array. */
function asKeyPersons(value: unknown): KeyPersonsValue | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    return undefined;
  const candidate = value as KeyPersonsValue;
  return Array.isArray(candidate.selectedIds) ? candidate : undefined;
}

function joinLabel(
  row: Record<string, unknown> | undefined,
  labelFields: string[],
  separator: string
): string {
  if (!row) return '';
  return labelFields
    .map((fieldId) => row[fieldId])
    .filter((part) => typeof part === 'string' && part.trim().length > 0)
    .join(separator)
    .trim();
}

/**
 * Turns the entries a person or repeatable field already holds into options for a later field.
 *
 * Fetches nothing — the entries are in form state, so options track edits as the user types. A
 * blank entry is still offered; it is a row the user added. Several named fields concatenate.
 */
export function buildFormFieldOptions(
  config: FormFieldSourceConfig,
  formData: Record<string, unknown>
): SelectOption[] {
  const separator = config.labelSeparator ?? ' ';
  const fallback = (index: number) => config.emptyLabel || `Entry ${index + 1}`;

  const optionsFrom = (value: unknown): SelectOption[] => {
    // A person chosen from the company authors no entry, so the name comes from the labels
    // the component publishes.
    const keyPersons = asKeyPersons(value);
    if (keyPersons) {
      const entries = keyPersons.entries ?? {};
      const labels = keyPersons[PERSON_LABELS_KEY] ?? {};
      return (keyPersons.selectedIds as unknown[]).reduce<SelectOption[]>(
        (acc, id, index) => {
          if (typeof id !== 'string' || id.length === 0) return acc;
          const published = labels[id];
          const label =
            (typeof published === 'string' && published.trim()) ||
            joinLabel(
              entries[id] as Record<string, unknown> | undefined,
              config.labelFields,
              separator
            ) ||
            fallback(index);
          acc.push({ value: id, label });
          return acc;
        },
        []
      );
    }

    if (!Array.isArray(value)) return [];

    return value.reduce<SelectOption[]>((acc, entry, index) => {
      if (!entry || typeof entry !== 'object') return acc;
      const row = entry as Record<string, unknown>;

      // No id, nothing stable to reference: an index shifts when a sibling is removed.
      const id = row[ENTRY_ID_KEY];
      if (typeof id !== 'string' || id.length === 0) return acc;

      acc.push({
        value: id,
        label: joinLabel(row, config.labelFields, separator) || fallback(index),
      });
      return acc;
    }, []);
  };

  // One person can be both a director and the responsible party; offer them once.
  const seen = new Set<string>();
  const options = [config.moduleId, ...(config.alsoFrom ?? [])]
    .flatMap((fieldId) => optionsFrom(formData[fieldId]))
    .filter((option) => {
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });

  return options;
}

export function useFormFieldOptions({
  config,
  formData,
  skip,
}: UseFormFieldOptionsParams): { options: SelectOption[] } {
  return useMemo(
    () => ({
      options: skip || !config ? [] : buildFormFieldOptions(config, formData),
    }),
    [config, formData, skip]
  );
}
